import { useState, useEffect } from 'react';
import {
  fetchAttachmentBlob,
  type Card as CardType,
  type CardAttachment,
  type RedraftReason,
  type EscalateTo,
} from '../hooks/useHatMail';
import type { OrderContextLookupStatus } from '../../../../types/order-context';
import { EditFlow, RedraftFlow, EscalateFlow, ClearFlow } from './CardActions';
import { useSwipe } from '../hooks/useSwipe';
import ConversationDrawer from './ConversationDrawer';

type ActiveFlow = 'edit' | 'redraft' | 'escalate' | 'clear' | null;
type EditFocus = 'response' | 'attachments';

interface CardProps {
  card: CardType;
  currentUserId?: string;
  onSend: (id: string) => void;
  onEdit: (id: string, body: string, attachmentIds?: string[]) => void;
  onRedraft: (id: string, reason: RedraftReason, note?: string) => void;
  onEscalate: (id: string, to: EscalateTo, note?: string) => void;
  onClear: (id: string, note?: string) => void;
  sending: boolean;
  editing: boolean;
  redrafting: boolean;
  escalating: boolean;
  clearing: boolean;
  isFirst?: boolean;
  showInboxTag?: boolean;
  readOnly?: boolean;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function confidenceBorder(confidence: number): string {
  if (confidence >= 0.9) return 'border-l-emerald-500';
  if (confidence >= 0.5) return 'border-l-amber-400';
  return 'border-l-red-400';
}

function senderInitial(name: string | null): string {
  if (!name) return '?';
  return name.charAt(0).toUpperCase();
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function ordinal(value: number): string {
  const lastTwo = value % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${value}th`;
  if (value % 10 === 1) return `${value}st`;
  if (value % 10 === 2) return `${value}nd`;
  if (value % 10 === 3) return `${value}rd`;
  return `${value}th`;
}

function cardStatusLabel(status: string): string {
  switch (status) {
    case 'sent':
      return 'Sent';
    case 'escalated':
      return 'Escalated';
    case 'auto_resolved':
      return 'Auto-resolved';
    case 'spam_filtered':
      return 'Filtered';
    case 'no_response':
      return 'Cleared, no reply';
    case 'handoff_cleared':
      return 'Cleared earlier';
    case 'locked':
      return 'Locked';
    default:
      return 'Pending';
  }
}

function orderContextLabel(status: OrderContextLookupStatus): string {
  switch (status) {
    case 'stage_found':
      return 'Verified order context';
    case 'order_found_no_stage':
      return 'Order found, stage unknown';
    case 'customer_not_found':
      return 'No Shopify customer match';
    case 'order_not_found':
      return 'No Shopify order match';
    case 'shopify_error':
      return 'Shopify lookup issue';
    case 'not_attempted':
    default:
      return 'No verified Shopify match';
  }
}

function OrderContextBlock({ card }: { card: CardType }) {
  const context = card.order_context;
  if (!context && card.category !== 'PROD_STATUS') return null;

  if (!context) {
    return (
      <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <p className="font-semibold">No verified Shopify match</p>
        <p className="mt-0.5">Do not promise order timing from this draft.</p>
      </div>
    );
  }

  const verified = context.lookup_status === 'stage_found';
  const placed = formatDate(context.order_created_at);
  const stageEntered = formatDate(context.stage_entered_at);

  return (
    <div className={`mb-3 rounded-lg border px-3 py-2 text-xs ${
      verified
        ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
        : context.anomaly_flag
          ? 'border-amber-200 bg-amber-50 text-amber-900'
          : 'border-gray-200 bg-gray-50 text-gray-700'
    }`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold">{orderContextLabel(context.lookup_status)}</p>
        <p className="text-[11px] opacity-75">{context.context_version}</p>
      </div>
      <div className="mt-1 grid gap-1 sm:grid-cols-2">
        {context.order_name && <p>Order: <span className="font-medium">{context.order_name}</span></p>}
        {placed && <p>Placed: <span className="font-medium">{placed}</span></p>}
        {context.production_stage && <p>Stage: <span className="font-medium">{context.production_stage}</span></p>}
        {stageEntered && <p>Stage date: <span className="font-medium">{stageEntered}</span></p>}
        {context.days_since_order !== null && <p>Order age: <span className="font-medium">{context.days_since_order}d</span></p>}
        {context.days_in_stage !== null && <p>Stage age: <span className="font-medium">{context.days_in_stage}d</span></p>}
      </div>
      {context.time_remaining && (
        <p className="mt-1">Approved timing: <span className="font-medium">{context.time_remaining}</span></p>
      )}
      {context.no_email_stage && (
        <p className="mt-1 font-medium">Internal stage. Review before customer send.</p>
      )}
      {context.anomaly_flag && (
        <p className="mt-1 font-medium">Team review: {context.anomaly_reason || 'Context requires review.'}</p>
      )}
      {!verified && (
        <p className="mt-1 font-medium">Do not guess timing from this draft.</p>
      )}
    </div>
  );
}

export default function EmailCard({
  card,
  currentUserId,
  onSend,
  onEdit,
  onRedraft,
  onEscalate,
  onClear,
  sending,
  editing,
  redrafting,
  escalating,
  clearing,
  isFirst,
  showInboxTag,
  readOnly = false,
}: CardProps) {
  const [flow, setFlow] = useState<ActiveFlow>(null);
  const [editFocus, setEditFocus] = useState<EditFocus>('response');
  const [collapsed, setCollapsed] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [viewer, setViewer] = useState<{ url: string; filename: string } | null>(null);
  const [loadingAttachmentId, setLoadingAttachmentId] = useState<string | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [conversationOpen, setConversationOpen] = useState(false);

  const isLockedByOther = !!(card.locked_by && card.locked_by !== currentUserId);
  const hasHumanDraft = !!(card.human_draft && card.human_draft_by !== currentUserId);
  const displayName = card.extracted_customer_name || card.from_name || card.from_email;

  const swipe = useSwipe(
    () => { if (!readOnly && !isLockedByOther) onSend(card.id); },
    () => { if (!readOnly && !isLockedByOther) onEscalate(card.id, 'foh_manager'); },
  );

  // Show swipe hint on first card only, once
  useEffect(() => {
    if (isFirst && !localStorage.getItem('hatmail_swipe_hint_shown')) {
      const isTouch = window.matchMedia('(pointer: coarse)').matches;
      if (isTouch) {
        setShowHint(true);
        localStorage.setItem('hatmail_swipe_hint_shown', 'true');
        const timer = setTimeout(() => setShowHint(false), 2500);
        return () => clearTimeout(timer);
      }
    }
  }, [isFirst]);

  useEffect(() => {
    return () => {
      if (viewer) URL.revokeObjectURL(viewer.url);
    };
  }, [viewer]);

  const closeViewer = () => {
    setViewer((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return null;
    });
  };

  const openAttachment = async (attachment: CardAttachment) => {
    setAttachmentError(null);
    setLoadingAttachmentId(attachment.id);
    try {
      const blob = await fetchAttachmentBlob(attachment.view_url);
      const url = URL.createObjectURL(blob);
      if (attachment.is_image) {
        setViewer((current) => {
          if (current) URL.revokeObjectURL(current.url);
          return { url, filename: attachment.filename };
        });
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.filename;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      setAttachmentError((error as Error).message || 'Could not open attachment');
    } finally {
      setLoadingAttachmentId(null);
    }
  };

  const openEditFlow = (focus: EditFocus = 'response') => {
    if (!isLockedByOther) {
      setEditFocus(focus);
      setFlow('edit');
    }
  };

  // Clamp offset for visual feedback
  const clampedOffset = Math.max(-200, Math.min(200, swipe.offset));
  const absOffset = Math.abs(clampedOffset);

  return (
    <>
      <div className="swipe-container" ref={readOnly ? undefined : swipe.ref}>
        {!readOnly && (
          <>
            <div
              className="swipe-backdrop swipe-backdrop-send"
              style={{ opacity: clampedOffset > 0 ? Math.min(absOffset / 80, 1) : 0 }}
            >
              Send
            </div>

            <div
              className="swipe-backdrop swipe-backdrop-escalate"
              style={{ opacity: clampedOffset < 0 ? Math.min(absOffset / 80, 1) : 0 }}
            >
              Escalate
            </div>
          </>
        )}

        <div
          className={`card-email border-l-4 ${confidenceBorder(card.confidence)} rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md ${showHint ? 'swipe-hint' : ''}`}
          style={{
            transform: !readOnly && swipe.swiping ? `translateX(${clampedOffset}px)` : undefined,
            transition: swipe.swiping ? 'none' : 'transform 200ms ease-out',
          }}
        >
        {/* Header row */}
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-ghw-cream text-sm font-bold text-ghw-brown">
              {senderInitial(displayName)}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-gray-900">{displayName}</p>
              <p className="truncate text-xs text-gray-500">
                {card.subject || '(no subject)'}
              </p>
            </div>
          </div>
          <span className="flex-shrink-0 text-xs text-gray-400">{timeAgo(card.received_at)}</span>
        </div>

        {/* Tags row */}
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
            {card.category.replace('_', ' ')}
          </span>
          {card.is_yellowstone && (
            <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              Yellowstone mention
            </span>
          )}
          {card.is_follow_up && (
            <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-600">
              {card.thread_depth > 1 ? `${ordinal(card.thread_depth)} reply` : '2nd reply'}
            </span>
          )}
          {card.priority_tier === 'urgent' && (
            <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-600">
              Urgent
            </span>
          )}
          {card.inbox === 'both' && (
            <span className="rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-600">
              retail@ + shop@
            </span>
          )}
          {showInboxTag && card.inbox !== 'both' && (
            <span className="rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-600">
              {card.inbox === 'shop' ? 'shop@' : 'retail@'}
            </span>
          )}
          {readOnly && (
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
              {cardStatusLabel(card.card_status)}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={() => setConversationOpen(true)}
          className="mb-3 inline-flex min-h-9 items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-ghw-brown transition hover:border-ghw-gold/50 hover:bg-ghw-cream/25"
        >
          View conversation ({card.conversation_message_count || Math.max(1, card.thread_depth + 1)})
        </button>

        {/* Attachments */}
        {card.attachments && card.attachments.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {card.attachments.map((att, i) => (
              <button
                key={att.id || i}
                type="button"
                onClick={() => openAttachment(att)}
                disabled={loadingAttachmentId === att.id}
                className="inline-flex max-w-full items-center gap-1 rounded-lg bg-gray-100 px-2.5 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-200 disabled:opacity-60"
                title={att.is_image ? 'Open image' : 'Download attachment'}
              >
                <span className="font-medium">{att.is_image ? 'Image' : 'File'}</span>
                <span className="max-w-[120px] truncate">{att.filename}</span>
                {att.size_bytes > 0 && (
                  <span className="text-gray-400">
                    ({att.size_bytes > 1048576
                      ? `${(att.size_bytes / 1048576).toFixed(1)} MB`
                      : `${Math.round(att.size_bytes / 1024)} KB`})
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        {attachmentError && (
          <p className="mb-2 text-xs text-red-500">{attachmentError}</p>
        )}

        <OrderContextBlock card={card} />

        {/* Email + Draft — always visible, tap header to collapse */}
        <div className="mb-3">
          {collapsed ? (
            <div
              className="cursor-pointer rounded-lg bg-gray-50 p-3"
              onClick={() => setCollapsed(false)}
            >
              <p className="text-sm leading-relaxed text-gray-700">
                &ldquo;{card.draft_body.length > 200 ? card.draft_body.slice(0, 200) + '...' : card.draft_body}&rdquo;
              </p>
              <p className="mt-1 text-xs text-ghw-gold">Tap to expand</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Original email from customer */}
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div
                  className="mb-1.5 flex items-center justify-between cursor-pointer"
                  onClick={() => setCollapsed(true)}
                >
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                    From {card.from_name || card.from_email}
                  </p>
                  <svg className="h-4 w-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                  </svg>
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-600">
                  {card.body_full || card.body_preview || 'No email body available'}
                </p>
              </div>
              {/* AI draft response */}
              <div className="rounded-lg bg-ghw-cream/40 border border-ghw-gold/20 p-3">
                <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ghw-gold">
                  Draft Response
                </p>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">
                  {card.draft_body}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Lock state */}
        {isLockedByOther && (
          <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
            <span>🔒</span>
            <span>{card.locked_by} is working on this</span>
          </div>
        )}

        {/* Human draft notice */}
        {hasHumanDraft && (
          <div className="mb-3 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <span className="mr-1">💬</span>
            {card.human_draft_by} started a draft
          </div>
        )}

        {/* Inline edit flow */}
        {flow === 'edit' && (
          <EditFlow
            card={card}
            onSave={(body, attachmentIds) => {
              onEdit(card.id, body, attachmentIds);
              setFlow(null);
            }}
            onCancel={() => setFlow(null)}
            saving={editing}
            autoFocusResponse={editFocus === 'response'}
          />
        )}

        {/* Actions */}
        {!readOnly && flow !== 'edit' && (
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
            {hasHumanDraft ? (
              <>
                <button
                  onClick={() => onSend(card.id)}
                  disabled={sending || isLockedByOther}
                  className="btn-touch btn-send col-span-2 sm:col-span-1 sm:flex-1"
                >
                  {sending ? 'Sending...' : "Send Draft"}
                </button>
                <button
                  onClick={() => openEditFlow('attachments')}
                  disabled={isLockedByOther}
                  className="btn-touch btn-outlined"
                >
                  Attach Image
                </button>
                <button
                  onClick={() => openEditFlow('response')}
                  disabled={isLockedByOther}
                  className="btn-touch btn-outlined"
                >
                  Edit
                </button>
                <button
                  onClick={() => setFlow('clear')}
                  disabled={isLockedByOther || clearing}
                  className="btn-touch btn-outlined"
                >
                  {clearing ? 'Clearing...' : 'Clear'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => onSend(card.id)}
                  disabled={sending || isLockedByOther}
                  className="btn-touch btn-send col-span-2 sm:col-span-1 sm:flex-1"
                >
                  {sending ? 'Sending...' : 'Send'}
                </button>
                <button
                  onClick={() => openEditFlow('attachments')}
                  disabled={isLockedByOther}
                  className="btn-touch btn-outlined"
                >
                  Attach Image
                </button>
                <button
                  onClick={() => openEditFlow('response')}
                  disabled={isLockedByOther}
                  className="btn-touch btn-outlined"
                >
                  Edit
                </button>
                <button
                  onClick={() => setFlow('redraft')}
                  disabled={isLockedByOther}
                  className="btn-touch btn-outlined"
                >
                  Redraft
                </button>
                <button
                  onClick={() => setFlow('escalate')}
                  disabled={isLockedByOther}
                  className="btn-touch btn-outlined-amber"
                >
                  Escalate
                </button>
                <button
                  onClick={() => setFlow('clear')}
                  disabled={isLockedByOther || clearing}
                  className="btn-touch btn-outlined"
                >
                  {clearing ? 'Clearing...' : 'Clear'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      </div>

      {viewer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={closeViewer}
        >
          <div
            className="flex max-h-full w-full max-w-4xl flex-col gap-3"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 text-white">
              <p className="truncate text-sm font-medium">{viewer.filename}</p>
              <div className="flex items-center gap-2">
                <a
                  href={viewer.url}
                  download={viewer.filename}
                  className="rounded-lg bg-white/15 px-3 py-2 text-sm hover:bg-white/25"
                >
                  Download
                </a>
                <button
                  type="button"
                  onClick={closeViewer}
                  className="rounded-lg bg-white/15 px-3 py-2 text-sm hover:bg-white/25"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="flex min-h-0 items-center justify-center">
              <img
                src={viewer.url}
                alt={viewer.filename}
                className="max-h-[82vh] max-w-full rounded-lg bg-white object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {flow === 'redraft' && (
        <RedraftFlow
          onSubmit={(reason, note) => {
            onRedraft(card.id, reason, note);
            setFlow(null);
          }}
          onCancel={() => setFlow(null)}
          loading={redrafting}
        />
      )}
      {flow === 'escalate' && (
        <EscalateFlow
          onSubmit={(to, note) => {
            onEscalate(card.id, to, note);
            setFlow(null);
          }}
          onCancel={() => setFlow(null)}
          loading={escalating}
        />
      )}
      {flow === 'clear' && (
        <ClearFlow
          onSubmit={(note) => {
            onClear(card.id, note);
            setFlow(null);
          }}
          onCancel={() => setFlow(null)}
          loading={clearing}
        />
      )}
      {conversationOpen && (
        <ConversationDrawer responseId={card.id} onClose={() => setConversationOpen(false)} />
      )}
    </>
  );
}
